import type { ActivityLogEntry } from "@/api/activity";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { useTTS } from "@/desktop/use-tts";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import type { ActivityLabel } from "@/lib/activity-label";
import { normalizeAssistantText } from "@/lib/assistant-text-normalization";
import { getErrorKindLabel } from "@/lib/error-kind-labels";
import { filingReceipt } from "@/lib/filing-receipt";
import { claimToastCooldown } from "@/lib/toast-cooldown-gate";
import { useActivityStore } from "@/stores/activity-store";
import { useAgentStore } from "@/stores/agent-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { frameNodeHidden } from "@/stores/canvas-frame-geometry";
import { useFramesStore } from "@/stores/frames-store";
import type { AvailableRuntime } from "@/stores/companion-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useConversationStore } from "@/stores/conversation-store";
import { useMemoryStore } from "@/stores/memory-store";
import type {
  SystemToast,
  SystemToastInput,
} from "@/stores/notification-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { isContextualDiscoveryPresentationHeld } from "@/desktop/onboarding/contextual-discovery-presentation";
import { usePointingStore } from "@/stores/pointing-store";
import { useProjectStore } from "@/stores/project-store";
import { useTasksStore } from "@/stores/tasks-store";
import { useWorkToastStore } from "@/stores/work-toast-store";
import { translate } from "@/i18n";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";

export { logTtsPipeline } from "@/desktop/tts-pipeline-log";

export function currentCursorDisplayId(): number | null {
  return useActiveDisplay.getState().activeDisplayId ?? getWindowDisplayId();
}

export function visibleConversationFrameForExecution(
  ctx: FrameApplyContext,
  executionId?: string,
  sessionId?: string,
  hintedFrameId?: string,
): string | null {
  const { framesById } = useFramesStore.getState();
  const { focusHiddenFrameIds } = ctx.canvasStore.getState();
  const isVisibleFrame = (frameId: string): boolean => {
    const frame = framesById[frameId];
    return !!frame && !frameNodeHidden(frame, focusHiddenFrameIds);
  };
  // Server-derived binding (cursor payload `conversation_frame_id`) is
  // authoritative when that frame is on canvas and visible — it does not
  // depend on the agentSessions race that session/execution inference has.
  if (hintedFrameId && isVisibleFrame(hintedFrameId)) return hintedFrameId;
  if (!executionId && !sessionId) return null;
  const sessions = Object.values(ctx.companionStore.getState().agentSessions);
  const trackedSession = executionId
    ? sessions.find((session) => session.executionId === executionId)
    : null;
  const resolvedSessionId = sessionId ?? trackedSession?.runtimeSessionId;
  const boundFrameId =
    sessions.find(
      (session) =>
        !!session.conversationFrameId &&
        ((executionId && session.executionId === executionId) ||
          (resolvedSessionId &&
            session.runtimeSessionId === resolvedSessionId)),
    )?.conversationFrameId ?? null;
  if (boundFrameId && isVisibleFrame(boundFrameId)) return boundFrameId;
  return null;
}

/** Whether the conversation store holds the run's messages or its chat's. */
function conversationHoldsExecution(
  ctx: FrameApplyContext,
  executionId: string,
): boolean {
  const conversationId = ctx.companionStore
    .getState()
    .executions.get(executionId)?.conversationId;
  return ctx.conversationStore
    .getState()
    .messages.some(
      (message) =>
        message.executionId === executionId ||
        (!!conversationId && message.conversationId === conversationId),
    );
}

export function sessionIdForExecution(
  ctx: FrameApplyContext,
  executionId: string | undefined,
  explicitSessionId: string | undefined,
): string | undefined {
  if (explicitSessionId) return explicitSessionId;
  if (!executionId) return undefined;
  return Object.values(ctx.companionStore.getState().agentSessions).find(
    (session) => session.executionId === executionId,
  )?.runtimeSessionId;
}

export function toolCallMessageId(
  executionId: string,
  toolCall: string,
  toolUseId: string | undefined,
  actionSeq: unknown,
  fallbackIndex: number,
): string {
  // The tool_use block id is the canonical key — history synthesis on the
  // backend uses the same scheme, so live and hydrated messages dedupe.
  if (toolUseId) return `tool-${executionId}-${toolUseId}`;
  if (typeof actionSeq === "number" && Number.isFinite(actionSeq)) {
    return `tool-${executionId}-${actionSeq}`;
  }
  const slug =
    toolCall
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "tool";
  return `tool-${executionId}-auto-${fallbackIndex}-${slug}`;
}

export function pushConversationToolCall(args: {
  ctx: FrameApplyContext;
  executionId?: string;
  sessionId?: string;
  conversationFrameId: string | null;
  toolCall?: string;
  toolCallLabel?: ActivityLabel;
  toolFailed?: boolean;
  toolUseId?: string;
  actionSeq: unknown;
  timestamp?: string;
}): void {
  if (!args.executionId || !args.toolCall) return;
  // MCP frame-action cursors carry an action sequence for animation, then the
  // canonical CLI tool event arrives with a stable tool-use id. Persist only
  // the canonical event so the disclosure does not show the same action twice.
  if (!args.toolUseId && typeof args.actionSeq === "number") return;
  const toolPrefix = `tool-${args.executionId}-`;
  const fallbackIndex =
    args.ctx.conversationStore
      .getState()
      .messages.filter((message) => message.id.startsWith(toolPrefix)).length +
    1;
  args.ctx.conversationStore.getState().pushMessage({
    id: toolCallMessageId(
      args.executionId,
      args.toolCall,
      args.toolUseId,
      args.actionSeq,
      fallbackIndex,
    ),
    role: "system",
    content: `Tool: ${args.toolCall}`,
    ...(args.toolCallLabel ? { contentLabel: args.toolCallLabel } : {}),
    activityKind: "tool_call",
    toolFailed: args.toolFailed,
    executionId: args.executionId,
    sessionId: args.sessionId,
    conversationFrameId: args.conversationFrameId ?? undefined,
    createdAt: args.timestamp ?? new Date().toISOString(),
  });
}

export function markConversationToolFailed(args: {
  ctx: FrameApplyContext;
  executionId?: string;
  toolUseId?: string;
}): void {
  if (!args.executionId || !args.toolUseId) return;
  args.ctx.conversationStore
    .getState()
    .markToolFailed(args.executionId, args.toolUseId);
}

export function pushConversationActivity(
  log: ActivityLogEntry,
  ctx: FrameApplyContext,
): void {
  if (
    log.target_type !== "execution" ||
    (log.action !== "agent.text" &&
      log.action !== "agent.narration" &&
      log.action !== "agent.desktop_interaction" &&
      log.action !== "agent.filing" &&
      log.action !== "agent.tool_error")
  ) {
    return;
  }
  const toolUseId =
    typeof log.details?.tool_use_id === "string" &&
    log.details.tool_use_id.trim()
      ? log.details.tool_use_id.trim()
      : undefined;
  if (log.action === "agent.tool_error") {
    markConversationToolFailed({
      ctx,
      executionId: log.target_id,
      toolUseId,
    });
    return;
  }
  const rawDescription = log.details?.text ?? log.details?.description;
  const description =
    typeof rawDescription === "string" && rawDescription.trim()
      ? rawDescription
      : undefined;
  const executionId = log.target_id;
  const sessionId = sessionIdForExecution(ctx, executionId, undefined);
  const conversationFrameId = visibleConversationFrameForExecution(
    ctx,
    executionId,
    sessionId,
  );
  // Whatever project is viewed, a chat the store holds keeps its activity
  // (Home's, the picked target's); the cursor fallback below needs a cursor.
  const chatKnowsRun =
    !!conversationFrameId || conversationHoldsExecution(ctx, executionId);
  if (log.action === "agent.desktop_interaction") {
    if (!chatKnowsRun) return;
    const receipt = log.details?.receipt;
    const desktopReceipt =
      receipt && typeof receipt === "object" && !Array.isArray(receipt)
        ? (receipt as Record<string, unknown>)
        : undefined;
    if (!desktopReceipt) return;
    ctx.conversationStore.getState().pushMessage({
      id: toolUseId
        ? `desktop-${executionId}-${toolUseId}`
        : `desktop-${executionId}-log-${log.id}`,
      role: "system",
      content: description ?? "Desktop interaction completed",
      activityKind: "desktop_interaction",
      desktopReceipt,
      executionId,
      sessionId,
      conversationFrameId: conversationFrameId ?? undefined,
      createdAt: log.created_at,
    });
    return;
  }
  if (log.action === "agent.filing") {
    const receipt = filingReceipt(log.details?.receipt);
    if (!chatKnowsRun || !receipt || !toolUseId) return;
    ctx.conversationStore.getState().pushMessage({
      id: `filing-${executionId}-${toolUseId}`,
      role: "system",
      content: description ?? receipt.project_name,
      activityKind: "filing",
      filingReceipt: receipt,
      executionId,
      sessionId,
      conversationFrameId: conversationFrameId ?? undefined,
      createdAt: log.created_at,
    });
    return;
  }
  if (!description) return;
  const stepId =
    typeof log.details?.step_id === "string" && log.details.step_id.trim()
      ? log.details.step_id
      : undefined;
  if (log.action === "agent.narration") {
    // Already spoken by the presentation as it played, and never streamed:
    // it joins the thread as speech, without a second voice or a draft to
    // replace.
    if (!chatKnowsRun) return;
    ctx.conversationStore.getState().pushMessage({
      id: `progress-${executionId}-log-${log.id}`,
      role: "system",
      content: description,
      activityKind: "progress",
      executionId,
      stepId,
      sessionId,
      conversationFrameId: conversationFrameId ?? undefined,
      createdAt: log.created_at,
    });
    return;
  }
  if (chatKnowsRun)
    ctx.conversationStore.getState().commitStreamingAssistantProgress({
      id: `progress-${executionId}-log-${log.id}`,
      role: "system",
      content: description,
      activityKind: "progress",
      executionId,
      stepId,
      sessionId,
      conversationFrameId: conversationFrameId ?? undefined,
      createdAt: log.created_at,
    });
  const cursorKey = resolveCursorKeyForExecution(ctx, executionId, log.project);
  const cursor = cursorKey
    ? ctx.canvasStore.getState().agentCursors.get(cursorKey)
    : undefined;
  if (!cursor) return;
  const normalizedDescription = normalizeAssistantText(description);
  ctx.canvasStore.getState().setAgentCursor({
    ...cursor,
    content: normalizedDescription,
    contentFull: normalizedDescription,
    lastSeen: log.created_at,
  });
  const origin =
    ctx.companionStore.getState().executions.get(executionId)?.origin ===
    "heartbeat"
      ? "heartbeat"
      : "chat";
  if (!isContextualDiscoveryPresentationHeld(executionId))
    ctx.ttsStore
      .getState()
      .speakStreamingReply(executionId, normalizedDescription, origin);
}

// ── Context ────────────────────────────────────────────────────

/**
 * Store handles and side-effect surfaces the reducer needs.
 *
 * Stores are passed as their Zustand store objects so the reducer can call
 * `.getState()` without React.  Explicit slots for non-store deps keep
 * the function testable and let Phase-4 mirror code substitute surfaces.
 */
export interface FrameApplyContext {
  companionStore: typeof useCompanionStore;
  canvasStore: typeof useCanvasStore;
  projectStore: typeof useProjectStore;
  activityStore: typeof useActivityStore;
  notificationStore: typeof useNotificationStore;
  tasksStore: typeof useTasksStore;
  memoryStore: typeof useMemoryStore;
  agentStore: typeof useAgentStore;
  pointingStore: typeof usePointingStore;
  ttsStore: typeof useTTS;
  onboardingStore: typeof useOnboardingStore;
  conversationStore: typeof useConversationStore;
  workToastStore: typeof useWorkToastStore;
  /** Cursor-dismiss timers keyed by the same cursorKey that agentCursors uses. */
  cursorRemovalTimers: Map<string, ReturnType<typeof setTimeout>>;
  /**
   * Run reducer work later. Every delayed store change goes through this,
   * never a bare `setTimeout`: a mirror applies frames with its mutations
   * held back from the owner, but only while the frame is being applied. A
   * bare timer fires after that, so a mirror would send its whole store -
   * possibly older than the owner's - up as the newest truth. That replaced
   * the owner's conversation, dropping a final answer that had just arrived.
   */
  defer: (work: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  /**
   * False on a mirror, which skips a frame's server-side effects - persisting
   * frame bindings, reloading history, refetching projects and memory. The
   * owner performs them: it receives the same frame and broadcasts the result,
   * while a mirror's copy would land after an await, outside the emit
   * suppression, and push its own (possibly stale) store up over the owner's.
   */
  ownsSideEffects?: boolean;
  addSystemToast: (toast: SystemToastInput, action?: () => void) => void;
}

// ── Utility ────────────────────────────────────────────────────

export function getErrorKindToast(
  errorKind: string,
  errMsg: string,
  execId: string,
): SystemToast | null {
  const label = getErrorKindLabel(errorKind, errMsg);
  if (!label) return null;
  // With no kind, only an overloaded provider's message yields a label.
  const prefix = errorKind ? "error" : "rate-limit";
  return { id: `${prefix}-${execId}`, ...label };
}

export function maybeToastCliUpdates(
  runtimes: AvailableRuntime[] | undefined,
  ctx: FrameApplyContext,
): void {
  // Web-only reminder. Desktop announces installed personal CLIs through
  // connection-health toasts, and the Denker updater owns bundled runtimes.
  const outdated = (runtimes ?? []).filter(
    (runtime) => runtime.update_available,
  );
  if (outdated.length === 0) return;
  // Rolling 24h (was calendar day).
  if (!claimToastCooldown("cli-update-reminder", 24 * 60 * 60_000)) return;
  const names: Record<string, string> = {
    claude_cli: "Claude Code",
    codex_cli: "Codex",
    opencode_cli: "OpenCode",
  };
  const description = outdated
    .map((r) => {
      const label = names[r.type] ?? r.type;
      return r.latest_version ? `${label} ${r.latest_version}` : label;
    })
    .join(", ");
  ctx.addSystemToast({
    id: `cli-update-reminder-${outdated.map((r) => `${r.type}:${r.latest_version ?? ""}`).join("|")}`,
    title: translate(libNoticesMessages, "cliUpdateTitle"),
    description: translate(libNoticesMessages, "cliUpdateDescription", {
      tools: description,
    }),
    level: "warning",
  });
}

export function resolveCursorKeyForExecution(
  ctx: FrameApplyContext,
  executionId: string | undefined,
  spaceId: string | undefined,
): string | null {
  if (!executionId) return null;
  const sessions = ctx.companionStore.getState().agentSessions;
  const cursors = ctx.canvasStore.getState().agentCursors;
  for (const cursor of cursors.values()) {
    if (cursor.executionId === executionId) {
      return cursor.agentId;
    }
  }
  for (const [sessionKey, session] of Object.entries(sessions)) {
    if (session.executionId !== executionId) continue;
    if (spaceId && session.spaceId !== spaceId) continue;
    if (session.runtimeSessionId && cursors.has(session.runtimeSessionId)) {
      return session.runtimeSessionId;
    }
    const agentKey = sessionKey.slice(0, sessionKey.lastIndexOf(":"));
    for (const cursor of cursors.values()) {
      const cursorName = (cursor.name ?? "").split(" · ")[0]?.toLowerCase();
      if (
        cursor.spaceId === session.spaceId &&
        cursorName === agentKey.toLowerCase()
      ) {
        return cursor.agentId;
      }
    }
  }
  return null;
}

// ── Per-family helpers ─────────────────────────────────────────
